import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface ResizableSidebarLayoutProps {
  sidebar: ReactNode;
  children: ReactNode;
  storageKey: string;
  minWidth: number;
  defaultWidth: number;
  maxWidth: number;
  sidebarClassName?: string;
  contentClassName?: string;
  handleClassName?: string;
  handleAriaLabel?: string;
}

function getInitialWidth({
  storageKey,
  minWidth,
  defaultWidth,
  maxWidth,
}: {
  storageKey: string;
  minWidth: number;
  defaultWidth: number;
  maxWidth: number;
}): number {
  const stored = localStorage.getItem(storageKey);
  if (stored) {
    const parsed = Number(stored);
    if (parsed >= minWidth && parsed <= maxWidth) return parsed;
  }
  return defaultWidth;
}

export function ResizableSidebarLayout({
  sidebar,
  children,
  storageKey,
  minWidth,
  defaultWidth,
  maxWidth,
  sidebarClassName,
  contentClassName,
  handleClassName,
  handleAriaLabel = 'Resize sidebar',
}: ResizableSidebarLayoutProps): React.ReactElement {
  const [width, setWidth] = useState(() =>
    getInitialWidth({ storageKey, minWidth, defaultWidth, maxWidth })
  );
  const isResizing = useRef(false);

  useEffect(() => {
    localStorage.setItem(storageKey, String(width));
  }, [storageKey, width]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent): void => {
      e.preventDefault();
      isResizing.current = true;
      const startX = e.clientX;
      const startWidth = width;

      Object.assign(document.body.style, {
        userSelect: 'none',
        cursor: 'col-resize',
      });

      const onMouseMove = (moveEvent: MouseEvent): void => {
        const newWidth = Math.min(
          maxWidth,
          Math.max(minWidth, startWidth + moveEvent.clientX - startX)
        );
        setWidth(newWidth);
      };

      const onMouseUp = (): void => {
        isResizing.current = false;
        Object.assign(document.body.style, {
          userSelect: '',
          cursor: '',
        });
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [maxWidth, minWidth, width]
  );

  return (
    <div className="flex flex-1 overflow-hidden">
      <div
        className={cn(
          'relative flex h-full flex-col border-r border-border bg-surface overflow-hidden',
          sidebarClassName
        )}
        style={{ width: `${String(width)}px`, flexShrink: 0 }}
      >
        {sidebar}
        <div
          role="separator"
          aria-label={handleAriaLabel}
          aria-orientation="vertical"
          tabIndex={0}
          onMouseDown={handleMouseDown}
          className={cn(
            'absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize bg-border/50 hover:bg-primary/40 transition-colors',
            handleClassName
          )}
        />
      </div>

      <div className={cn('flex flex-1 flex-col overflow-hidden', contentClassName)}>{children}</div>
    </div>
  );
}
